import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Caption1, formatHours, SkeletonLoader, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { useTaskSplits } from "~/repository/useTaskSplits";
import { updateTaskFromEventStream } from "~/store/actions/actions.tasks";
import { getIsFeatureEnabled } from "~/store/selectors/selector.configOptions";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import { useTaskSplitsRealTime } from "./hooks/useTaskSplitsRealtime";
import { SplitDialog } from "./SplitDialog";
import { useGlobalPrivateChannelListener } from "../Hooks/useGlobalPrivateChannelListener";
import TaskSplitDialog from "../Workload/components/TaskSplitDialog";

export const TaskSplits = ({ taskId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const task = useSelector((state) => getTaskById(state, taskId));
  const featureEnabled = useSelector((state) => getIsFeatureEnabled(state, "workload_plus"));
  const can = useSecurity();
  const canEdit = can(["edit"], task);
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useTaskSplits(taskId, {
    enabled: featureEnabled,
  });

  const [splitDialog, setSplitDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const isSplit = task && task?.is_split;

  const isTooltipDisabled = isSplit || !canEdit;

  const taskSplittable = useMemo(() => {
    if (!task) {
      return false;
    }
    if (task.start_on && task.due_on && task.estimate) {
      return task.due_on - task.start_on >= 86400;
    }
    return false;
  }, [task]);

  const hideSplitButton = useMemo(() => {
    if (!task) {
      return true;
    }
    if (task.start_on && task.due_on && task.estimate) {
      return task.due_on - task.start_on === 0;
    }
    return !canEdit;
  }, [task, canEdit]);

  const canSplit = taskSplittable && canEdit;

  const openSplitDialog = useCallback(() => {
    if (isSplit) {
      return setSplitDialog(true);
    }
    if (canSplit) {
      return setOpenConfirmDialog(true);
    }
  }, [canSplit, isSplit]);

  const closeSplitDialog = useCallback(() => {
    setSplitDialog(false);
  }, []);

  const split = useMemo(() => {
    if (data && data.daily_estimate) {
      return `${formatHours(data.daily_estimate)} h/d`;
    }
    if (data && data.splits?.length > 0) {
      return t(`{{count}} Splits`, {
        count: data.splits.length,
      });
    }
    return t("Split");
  }, [data, t]);

  const onSplitTask = useCallback(() => {
    queryClient.invalidateQueries(["workload-tasks"], { exact: false });
    setOpenConfirmDialog(false);
    refetch().then(() => {
      setSplitDialog(true);
    });
  }, [queryClient, refetch]);

  const handleClose = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const taskUpdatedHandler = useCallback(
    (data) => {
      if (data.id === taskId) {
        refetch();
      }
    },
    [refetch, taskId]
  );

  useGlobalPrivateChannelListener("TaskUpdated", taskUpdatedHandler);

  const taskSplitsRealtimeHandler = useCallback(
    (_data) => {
      if (_data.task && _data.task.id === taskId) {
        dispatch(updateTaskFromEventStream(_data.task));
        refetch();
      }
    },
    [dispatch, refetch, taskId]
  );

  useTaskSplitsRealTime(taskSplitsRealtimeHandler);

  if (!featureEnabled) {
    return null;
  }
  if (isLoggedUserClient) {
    return null;
  }
  if (hideSplitButton) {
    return null;
  }

  if (isLoading && !splitDialog) {
    return (
      <SkeletonLoader
        data-testid="task-split-loader"
        className="tw-w-12 tw-h-2"
      />
    );
  }

  return (
    <>
      <Tooltip
        title={canSplit ? t("Click to Split") : t("Set due date and time estimation first")}
        disable={isTooltipDisabled}
      >
        <Caption1
          color="theme-primary"
          className={classNames("tw-flex tw-items-center tw-justify-between", {
            "tw-opacity-50": (canEdit && !taskSplittable) || (!isSplit && !canEdit),
            "tw-cursor-pointer": isSplit || canSplit,
          })}
          aria-disabled={!canSplit}
          onClick={openSplitDialog}
        >
          {split}
        </Caption1>
      </Tooltip>
      <TaskSplitDialog
        open={openConfirmDialog}
        taskId={taskId}
        onSuccess={onSplitTask}
        onCancel={handleClose}
        context="sidebar"
      />
      <SplitDialog
        splits={data?.splits}
        taskId={taskId}
        open={splitDialog}
        onClose={closeSplitDialog}
      />
    </>
  );
};

TaskSplits.displayName = "TaskSplits";
TaskSplits.propTypes = {
  taskId: PropTypes.number.isRequired,
};
